/**
 * Addresses inside the iManage area, in iManage's own vocabulary:
 * libraries → workspaces (client/matter) → folders → documents → versions.
 *
 *   /$org/apps/imanage                          workspaces of the chosen library
 *   /$org/apps/imanage/w/$workspaceId           a workspace
 *   /$org/apps/imanage/w/$workspaceId/$folderId a folder (at any depth)
 *   /$org/apps/imanage/search | recent | favorites | saved | settings | about
 *
 * The open document, its version, the tab, the search text and the sort live
 * in the query string (`?doc=&v=&tab=&q=&sort=&dir=`), so every view is a
 * link and Back walks through what was opened.
 *
 * @module components/apps/imanage/imanagePaths
 */
import { defaultDirection, type IManageSortKey, type SortDirection } from "./imanageFormat";

export const IMANAGE_TABS = ["documents", "emails", "folders", "info"] as const;
export type IManageTab = (typeof IMANAGE_TABS)[number];

export const IMANAGE_SORT_KEYS = ["name", "version", "author", "modified", "size"] as const;

export interface IManageViewSearch {
  /** Open document (any version's ID). */
  readonly doc?: string;
  /** A specific version of the open document, when not the latest. */
  readonly v?: string;
  readonly tab?: IManageTab;
  readonly q?: string;
  readonly sort?: IManageSortKey;
  readonly dir?: "asc" | "desc";
  /** Library, on pages that are not inside a workspace. */
  readonly lib?: string;
}

/** iManage IDs: `LIBRARY!123` for containers, `LIBRARY!123.4` for a document version. */
const ID = /^[A-Za-z0-9_!.-]{1,200}$/;
export const isIManageId = (value: unknown): value is string =>
  typeof value === "string" && ID.test(value) && value !== "." && value !== "..";

/** The library an iManage ID names (`ACTIVE!5101` → `ACTIVE`), when it carries one. */
export function libraryOfId(id: string): string | null {
  const match = /^([A-Za-z0-9_-]{1,64})!/.exec(id);
  return match ? match[1]! : null;
}

/** One document across its versions: `ACTIVE!40211.7` → `ACTIVE!40211`. */
export const documentKey = (id: string): string => id.replace(/\.\d+$/, "");

/** The search params a route accepts; anything malformed is dropped, never trusted. */
export function parseIManageSearch(raw: Record<string, unknown>): IManageViewSearch {
  const out: {
    -readonly [K in keyof IManageViewSearch]?: IManageViewSearch[K];
  } = {};
  if (isIManageId(raw.doc)) out.doc = raw.doc;
  if (isIManageId(raw.v)) out.v = raw.v;
  if (IMANAGE_TABS.some((tab) => tab === raw.tab)) out.tab = raw.tab as IManageTab;
  if (typeof raw.q === "string" && raw.q.trim() && raw.q.length <= 500) out.q = raw.q;
  if (IMANAGE_SORT_KEYS.some((key) => key === raw.sort)) out.sort = raw.sort as IManageSortKey;
  if (raw.dir === "asc" || raw.dir === "desc") out.dir = raw.dir;
  if (typeof raw.lib === "string" && /^[A-Za-z0-9_-]{1,64}$/.test(raw.lib)) out.lib = raw.lib;
  return out;
}

export type IManageSection =
  | { readonly kind: "home" }
  | { readonly kind: "search" | "recent" | "favorites" | "saved" | "settings" | "about" }
  | { readonly kind: "workspace"; readonly workspaceId: string; readonly folderId: string | null }
  | { readonly kind: "other" };

/** Where in the area a pathname is, for the sidebar's selection. */
export function imanageSection(pathname: string): IManageSection | null {
  const segments = pathname.split("/").filter(Boolean);
  if (segments[1] !== "apps" || segments[2] !== "imanage") return null;
  const rest = segments.slice(3).map((segment) => {
    try {
      return decodeURIComponent(segment);
    } catch {
      return segment;
    }
  });
  if (rest.length === 0) return { kind: "home" };
  if (rest[0] === "w" && rest[1])
    return { kind: "workspace", workspaceId: rest[1], folderId: rest[2] ?? null };
  if (
    rest.length === 1 &&
    (["search", "recent", "favorites", "saved", "settings", "about"] as const).some(
      (x) => x === rest[0],
    )
  )
    return {
      kind: rest[0] as "search" | "recent" | "favorites" | "saved" | "settings" | "about",
    };
  return { kind: "other" };
}

/** Path of a workspace or one of its folders (for plain `<a href>` and copy-link). */
export function workspacePath(orgSegment: string, workspaceId: string, folderId?: string | null) {
  const base = `/${orgSegment}/apps/imanage/w/${encodeURIComponent(workspaceId)}`;
  return folderId ? `${base}/${encodeURIComponent(folderId)}` : base;
}

/**
 * The next search params: `patch` values replace, `null` or `""` removes a
 * key (so URLs never carry empty parameters), and absent keys are kept.
 */
export function patchSearch(
  previous: IManageViewSearch,
  patch: { readonly [K in keyof IManageViewSearch]?: IManageViewSearch[K] | null },
): IManageViewSearch {
  const next: Record<string, unknown> = { ...previous };
  for (const [key, value] of Object.entries(patch)) {
    if (value === undefined) continue;
    if (value === null || value === "") delete next[key];
    else next[key] = value;
  }
  return parseIManageSearch(next);
}

/** The table order a page's params ask for; newest first by default. */
export function sortFromSearch(search: IManageViewSearch): {
  readonly key: IManageSortKey;
  readonly dir: SortDirection;
} {
  const key = search.sort ?? "modified";
  return { key, dir: search.dir ?? defaultDirection(key) };
}
